import { normalizeSearchText } from "./searchText.js";

const SYNONYM_GROUPS = [
  ["creepy", "eerie", "unsettling"],
  ["spooky", "creepy"],
  ["slow burn", "slow-burn"],
  ["dystopian", "dystopia"],
  ["detectives", "detective", "investigator"],
  ["funny", "comedy", "comedic"],
  ["dark", "grim", "bleak"],
];

function buildSynonymGraph(groups = []) {
  const graph = new Map();

  for (const group of groups) {
    const normalizedForms = group.map((entry) => normalizeSearchText(entry)).filter(Boolean);
    for (const form of normalizedForms) {
      if (!graph.has(form)) {
        graph.set(form, new Set());
      }
      for (const other of normalizedForms) {
        if (other !== form) {
          graph.get(form).add(other);
        }
      }
    }
  }

  return graph;
}

const SYNONYM_GRAPH = buildSynonymGraph(SYNONYM_GROUPS);

function createQueryToken(token = "", { origin = token, kind = "exact", weight = 1 } = {}) {
  return {
    token,
    origin,
    kind,
    weight,
  };
}

function addToken(entryMap, entry) {
  const existing = entryMap.get(entry.token);
  if (!existing || existing.weight < entry.weight) {
    entryMap.set(entry.token, entry);
  }
}

function canPluralize(token = "") {
  return token.length > 4 && token.endsWith("s");
}

function findPluralBase(token = "", index = {}) {
  if (!canPluralize(token)) return null;
  const base = token.slice(0, -1);
  const titleTokens = index?.titleTokens || new Set();
  const tagTokens = index?.tagTokens || new Set();
  if (titleTokens.has(base) || tagTokens.has(base)) {
    return base;
  }
  return null;
}

export function expandQueryTokens(tokens = [], index = {}) {
  const queue = [];
  const expanded = new Map();
  const seen = new Set();

  for (const rawToken of tokens) {
    const token = normalizeSearchText(rawToken);
    if (!token || seen.has(token)) continue;
    const entry = createQueryToken(token);
    queue.push(entry);
    addToken(expanded, entry);
    seen.add(token);
  }

  for (let cursor = 0; cursor < queue.length; cursor += 1) {
    const current = queue[cursor];
    const pluralBase = findPluralBase(current.token, index);
    if (pluralBase && !expanded.has(pluralBase)) {
      const pluralEntry = createQueryToken(pluralBase, {
        origin: current.token,
        kind: "plural",
      });
      queue.push(pluralEntry);
      addToken(expanded, pluralEntry);
    }

    const directSynonyms = SYNONYM_GRAPH.get(current.token) || new Set();
    for (const synonym of directSynonyms) {
      if (!synonym || expanded.has(synonym)) continue;
      const synonymEntry = createQueryToken(synonym, {
        origin: current.token,
        kind: "synonym",
        weight: 0.7,
      });
      queue.push(synonymEntry);
      addToken(expanded, synonymEntry);
    }
  }

  return [...expanded.values()];
}

export function getQueryTokenMatchNote(entry = {}) {
  if (!entry || entry.kind === "exact") return "";
  if (entry.kind === "plural") {
    return ` (plural form of "${entry.origin}")`;
  }
  return ` (similar to "${entry.origin}")`;
}

export function getQueryTokenWeight(entry = {}) {
  return Number(entry.weight || 0);
}

export { SYNONYM_GROUPS };
